import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Brain, CheckCircle2, XCircle, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading, refresh, switchWorkspace } = useAuth();

  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        setState({ loading: false, invite: data, error: null });
      } catch (err) {
        setState({
          loading: false, invite: null,
          error: err.response?.data?.detail || "Invitation not found",
        });
      }
    })();
  }, [token]);

  const doAccept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      toast.success("You're in! Welcome aboard.");
      await refresh();
      try { await switchWorkspace(data.workspace_id); } catch {}
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invitation");
    } finally { setAccepting(false); }
  };

  if (state.loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Checking invitation…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6" data-testid="accept-invite-page">
      <div className="w-full max-w-md card-flat p-8">
        <div className="flex items-center gap-2 mb-6 text-muted-foreground">
          <Brain className="w-5 h-5" />
          <span className="font-display font-semibold text-foreground">RAGNES</span>
        </div>

        {state.error ? (
          <div className="space-y-4">
            <XCircle className="w-8 h-8 text-destructive" />
            <h1 className="font-display text-2xl">Invitation unavailable</h1>
            <p className="text-sm text-muted-foreground">{state.error}</p>
            <Link to="/login" className="inline-block">
              <Button className="rounded-full mt-2">Sign in instead</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="eyebrow">You're invited</p>
            <h1 className="font-display text-2xl leading-tight">
              Join <span className="text-foreground">{state.invite.workspace?.name}</span>
            </h1>
            <div className="text-sm text-muted-foreground">
              You'll join as <span className="font-medium text-foreground/90">{state.invite.role}</span>.
              {state.invite.email && (
                <> This invitation is for <span className="font-mono text-xs">{state.invite.email}</span>.</>
              )}
            </div>

            {user ? (
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground border border-border/60 rounded-md p-3 bg-secondary/30">
                  Accepting as <span className="font-medium text-foreground/90">{user.email}</span>
                </div>
                <Button onClick={doAccept} disabled={accepting} className="rounded-full w-full" data-testid="accept-invite-btn">
                  {accepting ? "Joining…" : <><CheckCircle2 className="w-4 h-4 mr-2" /> Accept invitation</>}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Sign in or create an account to accept.</p>
                <div className="flex gap-2">
                  <Link to={`/login?next=/invite/${token}`} className="flex-1">
                    <Button className="rounded-full w-full" data-testid="accept-invite-login-btn"><LogIn className="w-4 h-4 mr-2" /> Sign in</Button>
                  </Link>
                  <Link to={`/register?next=/invite/${token}`} className="flex-1">
                    <Button variant="outline" className="rounded-full w-full" data-testid="accept-invite-register-btn">Register</Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
